// =====================================================================================
// LiveKitConnectionManager - Manages LiveKit Room lifecycle and connections
// =====================================================================================
// Handles Room instantiation, connection/disconnection, and event management
// Provides a clean interface for LiveKit operations

import { AudioPresets, Room, RoomEvent, VideoPresets } from 'livekit-client';
import type { VideoEncoding } from 'livekit-client';
import type {
  ILiveKitConnectionManager,
  ConnectionConfig,
  DisconnectReason
} from './types.js';

// Performance-optimized video quality presets
export enum VideoQualityPreset {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  ADAPTIVE = 'adaptive'
}

// Give up reconnecting after this many consecutive failures. LiveKit has no
// `maxRetryCount` option — a policy stops retrying by returning null.
const MAX_RECONNECT_ATTEMPTS = 5;

export class LiveKitConnectionManager implements ILiveKitConnectionManager {
  private room: Room | null = null;
  private connectionTimeout: NodeJS.Timeout | null = null;
  private connectionSuccess = false;
  private isConnectingFlag = false;
  private qualityPreset: VideoQualityPreset = VideoQualityPreset.ADAPTIVE;

  // Event callbacks that can be set by the consumer
  /**
   * Fired when the room reaches the connected state. The room is handed over
   * explicitly rather than left to be fetched with `getRoom()`: consumers used
   * to read `null` here, because `RoomEvent.Connected` arrives while
   * `connect()` is still awaiting (#77).
   */
  public onConnected?: (room: Room) => void;
  public onDisconnected?: (reason?: DisconnectReason) => void;
  public onReconnecting?: () => void;
  public onReconnected?: () => void;
  public onParticipantConnected?: () => void;
  public onParticipantDisconnected?: () => void;
  /**
   * Fired when the browser starts or stops allowing audio playback. A `false`
   * value means autoplay is blocked and `startAudio()` has to be called from a
   * user gesture before remote participants become audible.
   */
  public onAudioPlaybackStatusChanged?: (canPlaybackAudio: boolean) => void;

  private readonly defaultConfig: ConnectionConfig = {
    adaptiveStream: true,
    dynacast: true,
    publishDefaults: {
      // Must be an AudioPreset object — a string silently overrides LiveKit's
      // AudioPresets.music default with an undefined bitrate.
      audioPreset: AudioPresets.speech,
      videoEncoding: this.getVideoEncoding(VideoQualityPreset.ADAPTIVE),
      simulcast: true, // Enable simulcast for adaptive quality
      stopMicTrackOnMute: true
    },
    // Microphone capture constraints. These belong here, not under
    // publishDefaults: TrackPublishDefaults has no audio-capture keys.
    audioCaptureDefaults: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true
    },
    // Connection optimization
    reconnectPolicy: {
      nextRetryDelayInMs: (context) => {
        if (context.retryCount >= MAX_RECONNECT_ATTEMPTS) {
          return null; // Stop retrying
        }
        // Exponential backoff with jitter
        const baseDelay = Math.min(1000 * Math.pow(2, context.retryCount), 30000);
        const jitter = Math.random() * 0.3; // Add up to 30% jitter
        return baseDelay * (1 + jitter);
      }
    },
    // Bandwidth optimization
    videoCaptureDefaults: {
      resolution: VideoPresets.h540.resolution,
      facingMode: 'user'
    }
    // No `e2ee`: end-to-end encryption stays off. (The old `e2eeEnabled: false`
    // was not a RoomOptions key and did nothing at all.)
  };

  constructor(qualityPreset: VideoQualityPreset = VideoQualityPreset.ADAPTIVE) {
    this.qualityPreset = qualityPreset;
    console.info('🎥 LiveKitConnectionManager initialized with quality preset:', qualityPreset);
  }

  /**
   * Get optimized video encoding settings based on quality preset.
   *
   * `priority` is an `RTCPriorityType` ('very-low' | 'low' | 'medium' |
   * 'high'); 'standard' is not one of them and used to be accepted only
   * because the config was untyped.
   */
  private getVideoEncoding(preset: VideoQualityPreset): VideoEncoding {
    switch (preset) {
      case VideoQualityPreset.LOW:
        return {
          maxBitrate: 300_000, // 300 Kbps
          maxFramerate: 15,
          priority: 'low'
        };
      case VideoQualityPreset.MEDIUM:
        return {
          maxBitrate: 800_000, // 800 Kbps
          maxFramerate: 24,
          priority: 'medium'
        };
      case VideoQualityPreset.HIGH:
        return {
          maxBitrate: 2_000_000, // 2 Mbps
          maxFramerate: 30,
          priority: 'high'
        };
      case VideoQualityPreset.ADAPTIVE:
      default:
        return {
          maxBitrate: 1_200_000, // 1.2 Mbps - good balance
          maxFramerate: 24,
          priority: 'medium'
        };
    }
  }

  async connect(url: string, token: string, config?: ConnectionConfig): Promise<Room> {
    // Check if already connected or connecting
    if (this.room) {
      if (this.room.state === 'connected') {
        console.info('🎥 Already connected to a room, returning existing room');
        return this.room;
      }
      if (this.room.state === 'connecting') {
        console.warn('🎥 Room is still connecting, skipping duplicate connection attempt');
        throw new Error('CONNECTION_IN_PROGRESS');
      }
      // Disconnect stale room
      await this.disconnect();
    }

    if (this.isConnectingFlag) {
      console.warn('🎥 Connection already in progress');
      throw new Error('CONNECTION_IN_PROGRESS');
    }

    this.isConnectingFlag = true;
    this.connectionSuccess = false;

    // Create and configure room with merged config and quality-specific settings
    const qualityConfig: ConnectionConfig = {
      ...this.defaultConfig,
      publishDefaults: {
        ...this.defaultConfig.publishDefaults,
        videoEncoding: this.getVideoEncoding(this.qualityPreset)
      }
    };
    const finalConfig: ConnectionConfig = { ...qualityConfig, ...config };
    const newRoom = new Room(finalConfig);

    // Set up event handlers
    this.setupEventHandlers(newRoom);

    // Publish the room *before* awaiting the connect. `RoomEvent.Connected`
    // fires during that await, so assigning afterwards left `getRoom()`
    // returning null for the whole of `onConnected` — which is where
    // performance monitoring starts (#77).
    this.room = newRoom;

    // Set connection timeout
    this.connectionTimeout = setTimeout(async () => {
      if (!this.connectionSuccess) {
        console.error('🎥 Connection timeout - failed to connect within 15 seconds');
        this.isConnectingFlag = false;

        // Clean up the failed room
        try {
          await newRoom.disconnect();
        } catch (err) {
          console.warn('🎥 Error disconnecting timed-out room:', err);
        }

        this.room = null;
        throw new Error('CONNECTION_TIMEOUT');
      }
    }, 15000); // 15 second timeout

    try {
      console.info('🎥 Connecting to LiveKit room...');
      console.debug('🎥 Connection details:', { url, tokenLength: token?.length || 0 });

      await newRoom.connect(url, token);

      // Clear timeout on successful connection
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }

      this.isConnectingFlag = false;

      console.info('🎥 Successfully connected to LiveKit room with quality preset:', this.qualityPreset);

      return newRoom;
    } catch (error) {
      // Clear timeout on error
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }

      this.isConnectingFlag = false;
      console.error('🎥 Failed to connect to video room:', error);

      // Clean up the failed room connection
      try {
        await newRoom.disconnect();
      } catch (err) {
        console.warn('🎥 Error disconnecting failed room:', err);
      }

      this.room = null;
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    if (this.room) {
      console.info('🎥 Disconnecting from LiveKit room');
      try {
        await this.room.disconnect();
      } catch (err) {
        console.warn('🎥 Error during disconnect:', err);
      }
      this.room = null;
      this.connectionSuccess = false;
      this.isConnectingFlag = false;
    }
  }

  getRoom(): Room | null {
    return this.room;
  }

  isConnected(): boolean {
    return this.room?.state === 'connected' || false;
  }

  isConnecting(): boolean {
    return this.isConnectingFlag || this.room?.state === 'connecting' || false;
  }

  /**
   * Whether the browser is currently allowing audio playback.
   *
   * With no room we report `true`: there is nothing being blocked yet, and a
   * consumer should not show an "unblock audio" prompt before a call starts.
   */
  canPlaybackAudio(): boolean {
    return this.room?.canPlaybackAudio ?? true;
  }

  /**
   * Resume blocked audio playback. Browsers only honour this inside a user
   * gesture, so call it from a click/tap handler.
   */
  async startAudio(): Promise<void> {
    if (!this.room) {
      return;
    }
    try {
      await this.room.startAudio();
      console.info('🎥 Audio playback started');
    } catch (error) {
      console.warn('🎥 Failed to start audio playback:', error);
      throw error;
    }
  }

  private setupEventHandlers(room: Room): void {
    room.on(RoomEvent.Connected, () => {
      console.info('🎥 Connected to video room successfully');
      console.debug('🎥 Connection details:', {
        participants: room.remoteParticipants?.size || 0,
        localParticipant: room.localParticipant?.identity || 'unknown'
      });

      this.connectionSuccess = true;
      this.onConnected?.(room);
    });

    room.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
      console.info('🎥 Disconnected from video room:', reason);
      console.debug('🎥 Disconnect details:', {
        reason,
        wasConnected: this.connectionSuccess
      });

      this.connectionSuccess = false;
      this.isConnectingFlag = false;
      this.onDisconnected?.(reason);
    });

    room.on(RoomEvent.Reconnecting, () => {
      console.info('🎥 Reconnecting to video room...');
      this.onReconnecting?.();
    });

    room.on(RoomEvent.Reconnected, () => {
      console.info('🎥 Reconnected to video room');
      this.onReconnected?.();
    });

    room.on(RoomEvent.ParticipantConnected, () => {
      this.onParticipantConnected?.();
    });

    room.on(RoomEvent.ParticipantDisconnected, () => {
      this.onParticipantDisconnected?.();
    });

    // Autoplay policies can block remote audio until the user interacts with
    // the page. Surface that so the UI can offer an "unblock audio" affordance.
    room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      const canPlaybackAudio = room.canPlaybackAudio;
      if (canPlaybackAudio) {
        console.info('🎥 Audio playback is allowed');
      } else {
        console.warn('🎥 Audio playback is blocked — a user gesture is required');
      }
      this.onAudioPlaybackStatusChanged?.(canPlaybackAudio);
    });
  }

  // Clean up resources
  cleanup(): void {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    if (this.room) {
      this.room.disconnect().catch(err => {
        console.warn('🎥 Error during cleanup disconnect:', err);
      });
      this.room = null;
    }

    this.connectionSuccess = false;
    this.isConnectingFlag = false;
  }
}